import AnimatedPage from "@/components/AnimatedPage";
import { Helmet } from "react-helmet-async";
import LandingHeader from "@/components/landing/LandingHeader";
import LandingFooter from "@/components/landing/LandingFooter";

const Refund = () => {
  return (
    <AnimatedPage>
      <Helmet>
        <title>Refund Policy | Chito - Chit Fund Management Platform</title>
        <meta name="description" content="Understand Chito's refund policy for our chit fund management services. Learn about eligibility, processing times, and how to request a refund." />
        <meta name="keywords" content="chit fund refund, refund policy, chito refunds, financial service refunds, subscription refunds" />
        <link rel="canonical" href="https://chito.in/refund" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Refund Policy",
            "description": "Refund Policy for the Chito Chit Fund Management Platform",
            "publisher": {
              "@type": "Organization",
              "name": "Chito",
              "logo": "https://chito.in/logo.png"
            }
          })}
        </script>
      </Helmet>
      <div className="min-h-screen flex flex-col">
        <LandingHeader />

        <main className="container border-t mx-auto py-10 px-4 mt-16">
          <article className="max-w-3xl mx-auto text-left">
            <h1 className="text-3xl font-bold mb-8">Refund Policy</h1>
            
            <section className="mb-8" aria-labelledby="intro">
              <h2 id="intro" className="text-xl font-semibold mb-4">1. Introduction</h2>
              <p className="mb-4">At Chito, we strive to ensure customer satisfaction with our chit fund management services. This Refund Policy outlines the conditions under which we issue refunds for our subscription plans and services.</p>
            </section>
            
            <section className="mb-8" aria-labelledby="eligibility">
              <h2 id="eligibility" className="text-xl font-semibold mb-4">2. Refund Eligibility</h2>
              <p className="mb-4">We offer refunds under the following circumstances:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Service subscriptions cancelled within 14 days of initial purchase</li>
                <li>Technical issues preventing service delivery that we cannot resolve</li>
                <li>Billing errors or duplicate charges</li>
                <li>Service unavailability for extended periods</li>
              </ul>
            </section>
            
            <section className="mb-8" aria-labelledby="non-eligible">
              <h2 id="non-eligible" className="text-xl font-semibold mb-4">3. Non-Eligible Cases</h2>
              <p className="mb-4">Refunds are not provided in the following cases:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Subscriptions cancelled after the 14-day cooling-off period</li>
                <li>Service dissatisfaction without prior attempt to resolve issues with our support team</li>
                <li>User violations of our Terms of Service</li>
                <li>Changes in personal circumstances or financial situations</li>
              </ul>
            </section>
            
            <section className="mb-8" aria-labelledby="process">
              <h2 id="process" className="text-xl font-semibold mb-4">4. Refund Process</h2>
              <p className="mb-4">To request a refund:</p>
              <ol className="list-decimal pl-6 space-y-2">
                <li>Contact our support team at <a href="mailto:support@chito.in" className="text-primary hover:underline">support@chito.in</a></li>
                <li>Provide your account details and reason for the refund request</li>
                <li>Our team will review your request within 5 business days</li>
                <li>If approved, refunds will be processed to the original payment method</li>
              </ol>
            </section>
            
            <section className="mb-8" aria-labelledby="timeframe">
              <h2 id="timeframe" className="text-xl font-semibold mb-4">5. Processing Timeframe</h2>
              <p className="mb-4">Refunds typically take 7-14 business days to appear in your account, depending on your payment provider's processing times.</p>
            </section>
            
            <section className="mb-8" aria-labelledby="modifications">
              <h2 id="modifications" className="text-xl font-semibold mb-4">6. Policy Modifications</h2>
              <p className="mb-4">Chito reserves the right to modify this refund policy at any time. Changes will be effective immediately upon posting on our website.</p>
            </section>
            
            <section className="mb-8" aria-labelledby="contact-us">
              <h2 id="contact-us" className="text-xl font-semibold mb-4">7. Contact Us</h2>
              <p className="mb-4">If you have any questions about our Refund Policy, please contact our support team at <a href="mailto:support@chito.in" className="text-primary hover:underline">support@chito.in</a>.</p>
            </section>
            
            <p className="text-sm text-muted-foreground mt-12">Last updated: April 20, 2025</p>
          </article>
        </main>
        
        <LandingFooter />
      </div>
    </AnimatedPage>
  );
};

export default Refund;
